/**
 * Espace de travail AGORA : pages ouvertes (dock › Pages), page active et affichage côte à côte.
 *
 * - Une PAGE = un écran avec son propre routeur (CadrePage) et son propre historique ; toutes les pages ouvertes
 *   restent montées (masquées quand elles ne sont pas visibles) : filtres, sélection, défilement et brouillons
 *   sont conservés. 8 pages au maximum (la plus ancienne non visible se ferme, confirmation si brouillon).
 * - Clic simple (lien, menu, recherche, fil d'Ariane, retour) : navigation DANS la page active (ou dans la page
 *   où se trouve le lien). Ctrl/⌘ + clic sur un lien interne, ou bouton « Ouvrir dans une nouvelle page »
 *   (<LienPage>) : nouvelle page au premier plan ; l'ancienne glisse dans « Pages » du dock (animation).
 * - Côte à côte : 2 volets (bouton « + » du panneau Pages), séparateur réglable ; le volet actif reçoit la
 *   navigation de la barre latérale, de la recherche et du fil d'Ariane ; l'URL du navigateur suit la page active.
 * - L'espace (pages, disposition) est enregistré par utilisateur (localStorage) et restauré au rechargement ou à la
 *   reconnexion ; les brouillons non enregistrés ne le sont pas.
 */
import React from 'react';
import { DIMENSIONS, type MetierId } from '@agora/shared';
import { courbeCss, DUREES, mouvementReduit, zoomRacine } from '../../design';
import { useT } from '../i18n';
import { useSocle } from '../contexte';
import { decrirePage } from '../shell/page';
import { useConfirmation } from '../retours/Retours';
import { SourcesBrouillon } from './brouillons';
import { adresseCourante, estRouteAuth } from './navigateur';
import { activitesEnCours, autoriserSortie, sortieAutorisee } from './sortie';
import { transitionEcran } from './transition-ecran';

export const PAGES_MAX = 8;
const HISTORIQUE_MAX = 30;
export const RATIO_MIN = 30;
export const RATIO_MAX = 70;
/** Copie de la page vers le dock (animations, plus bas) : 1,5 × durée longue = 480 ms. */
const DUREE_ANIMATION = DUREES.long * 1.5;
/** Pulsation du bouton « Pages » (.agx-pulse de core/app.css) : 2 × durée longue = 640 ms. */
const DUREE_PULSATION = DUREES.long * 2;

export interface PageEspace {
  id: string;
  historique: string[];      // adresses (chemin + paramètres + ancre)
  index: number;             // position dans l'historique
  titre?: string;            // titre fourni par l'écran (useTitrePage)
  etat?: string;             // résumé de l'état (useEtatPage)
  brouillon: boolean;        // brouillon non enregistré (useBrouillon) – jamais enregistré
  vueLe: number;             // dernière fois visible (ms)
}

export const routeDe = (p: PageEspace): string => p.historique[p.index] || '/';
export const cheminSeul = (route: string): string => route.split(/[?#]/)[0] || '/';
/** La navigation vers `cible` change d'écran dans la page (et non seulement ses filtres, sa recherche ou son ancre). */
export const changementDEcran = (p: PageEspace, cible: string | undefined): boolean =>
  !!cible && cheminSeul(cible) !== cheminSeul(routeDe(p));

interface Etat {
  pages: PageEspace[];
  volets: string[];          // 1 ou 2 pages visibles
  actif: number;             // volet actif (0 | 1)
  ratio: number;             // largeur du volet de gauche (%)
  op: { type: 'push' | 'replace'; page: string } | null;   // dernière navigation (synchronisation de l'URL)
  entree?: string;           // page qui vient d'apparaître (animation)
}

type Action =
  | { type: 'naviguer'; page: string; route: string; remplacer: boolean }
  | { type: 'aller'; page: string; delta: number }
  | { type: 'ouvrir'; page: PageEspace; aCote: boolean; fermer?: string }
  | { type: 'afficher'; page: string }
  | { type: 'aCote'; page: string }
  | { type: 'fermer'; page: string; accueil: PageEspace }
  | { type: 'seule'; volet: number }
  | { type: 'activer'; volet: number }
  | { type: 'ratio'; valeur: number }
  | { type: 'infos'; page: string; infos: Partial<Pick<PageEspace, 'titre' | 'etat' | 'brouillon'>> }
  | { type: 'historique'; page: string; index: number; route: string }
  | { type: 'finEntree' };

const nouvelId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `p${Date.now()}${Math.random().toString(36).slice(2)}`);
export const nouvellePage = (route: string): PageEspace => ({ id: nouvelId(), historique: [route], index: 0, brouillon: false, vueLe: Date.now() });
const borner = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/** Horodate les pages visibles (choix de la page la plus ancienne à fermer). */
function marquer(e: Etat): Etat {
  const maintenant = Date.now();
  return { ...e, pages: e.pages.map((p) => (e.volets.includes(p.id) ? { ...p, vueLe: maintenant } : p)) };
}

function naviguerDans(p: PageEspace, route: string, remplacer: boolean): PageEspace {
  if (routeDe(p) === route) return p;
  if (remplacer) {
    const historique = [...p.historique];
    historique[p.index] = route;
    return { ...p, historique };
  }
  const historique = [...p.historique.slice(0, p.index + 1), route].slice(-HISTORIQUE_MAX);
  return { ...p, historique, index: historique.length - 1 };
}

function reducteur(e: Etat, a: Action): Etat {
  switch (a.type) {
    case 'naviguer': {
      const p = e.pages.find((x) => x.id === a.page);
      if (!p) return e;
      const n = naviguerDans(p, a.route, a.remplacer);
      if (n === p) return e;
      return { ...e, pages: e.pages.map((x) => (x.id === a.page ? n : x)), op: { type: a.remplacer ? 'replace' : 'push', page: a.page } };
    }
    case 'aller': {
      const p = e.pages.find((x) => x.id === a.page);
      if (!p) return e;
      const index = borner(p.index + a.delta, 0, p.historique.length - 1);
      if (index === p.index) return e;
      return { ...e, pages: e.pages.map((x) => (x.id === a.page ? { ...x, index } : x)), op: { type: 'replace', page: a.page } };
    }
    case 'ouvrir': {
      const pages = [...e.pages.filter((p) => p.id !== a.fermer), a.page];
      let volets = e.volets.filter((id) => id !== a.fermer);
      let actif = Math.min(e.actif, volets.length - 1);
      if (a.aCote && volets.length === 1) { volets = [volets[0], a.page.id]; actif = 1; }
      else if (a.aCote) { actif = 1 - actif; volets = volets.map((id, i) => (i === actif ? a.page.id : id)); }
      else volets = volets.map((id, i) => (i === actif ? a.page.id : id));
      return marquer({ ...e, pages, volets, actif, op: { type: 'push', page: a.page.id }, entree: a.page.id });
    }
    case 'afficher': {
      if (!e.pages.some((p) => p.id === a.page)) return e;
      const i = e.volets.indexOf(a.page);
      if (i === e.actif) return e;
      if (i >= 0) return marquer({ ...e, actif: i, op: { type: 'replace', page: a.page } });
      return marquer({ ...e, volets: e.volets.map((id, j) => (j === e.actif ? a.page : id)), op: { type: 'replace', page: a.page } });
    }
    case 'aCote': {
      if (!e.pages.some((p) => p.id === a.page) || e.volets[e.actif] === a.page) return e;
      if (e.volets.length === 1) return marquer({ ...e, volets: [e.volets[0], a.page], actif: 1, op: { type: 'replace', page: a.page } });
      const i = 1 - e.actif;
      return marquer({ ...e, volets: e.volets.map((id, j) => (j === i ? a.page : id)), actif: i, op: { type: 'replace', page: a.page } });
    }
    case 'fermer': {
      const pages = e.pages.filter((p) => p.id !== a.page);
      const i = e.volets.indexOf(a.page);
      if (i < 0) return { ...e, pages };
      if (e.volets.length === 2) {
        const reste = e.volets[1 - i];
        return marquer({ ...e, pages, volets: [reste], actif: 0, op: { type: 'replace', page: reste } });
      }
      const suivante = [...pages].sort((x, y) => y.vueLe - x.vueLe)[0];
      if (suivante) return marquer({ ...e, pages, volets: [suivante.id], actif: 0, op: { type: 'replace', page: suivante.id } });
      return marquer({ ...e, pages: [a.accueil], volets: [a.accueil.id], actif: 0, op: { type: 'replace', page: a.accueil.id } });
    }
    case 'seule': {
      const id = e.volets[a.volet];
      if (!id || e.volets.length === 1) return e;
      return marquer({ ...e, volets: [id], actif: 0, op: { type: 'replace', page: id } });
    }
    case 'activer':
      if (a.volet === e.actif || a.volet >= e.volets.length) return e;
      return { ...e, actif: a.volet, op: { type: 'replace', page: e.volets[a.volet] } };
    case 'ratio': {
      const ratio = borner(Math.round(a.valeur), RATIO_MIN, RATIO_MAX);
      return ratio === e.ratio ? e : { ...e, ratio };
    }
    case 'infos': {
      const p = e.pages.find((x) => x.id === a.page);
      if (!p) return e;
      const change = (Object.keys(a.infos) as (keyof typeof a.infos)[]).some((k) => p[k] !== a.infos[k]);
      return change ? { ...e, pages: e.pages.map((x) => (x.id === a.page ? { ...x, ...a.infos } : x)) } : e;
    }
    case 'historique': {
      // Retour / avance du navigateur : on retrouve la page et sa position ; sinon l'adresse s'applique à la page active.
      const p = e.pages.find((x) => x.id === a.page);
      if (!p) {
        const id = e.volets[e.actif];
        return { ...e, pages: e.pages.map((x) => (x.id === id ? naviguerDans(x, a.route, true) : x)), op: null };
      }
      const index = borner(a.index, 0, p.historique.length - 1);
      const pages = e.pages.map((x) => (x.id === a.page ? { ...x, index } : x));
      const i = e.volets.indexOf(a.page);
      if (i >= 0) return { ...e, pages, actif: i, op: null };
      return marquer({ ...e, pages, volets: e.volets.map((id, j) => (j === e.actif ? a.page : id)), op: null });
    }
    case 'finEntree':
      return e.entree ? { ...e, entree: undefined } : e;
  }
}

/* ---------------------------------------------------------------- restauration */
interface Sauvegarde { v: 1; pages: Omit<PageEspace, 'brouillon'>[]; volets: string[]; actif: number; ratio: number }
const cleEspace = (idUtilisateur: string) => `agora.espace.${idUtilisateur}`;

function initialiser(idUtilisateur: string): Etat {
  const adresse = adresseCourante();
  const route = estRouteAuth(cheminSeul(adresse)) ? '/' : adresse;
  let etat: Etat | null = null;
  try {
    const s = JSON.parse(localStorage.getItem(cleEspace(idUtilisateur)) || 'null') as Sauvegarde | null;
    if (s && s.v === 1 && Array.isArray(s.pages) && s.pages.length) {
      const pages = s.pages
        .filter((p) => p && typeof p.id === 'string' && Array.isArray(p.historique) && p.historique.length && p.historique.every((h) => typeof h === 'string' && h.startsWith('/')))
        .slice(0, PAGES_MAX)
        .map((p) => ({ ...p, index: borner(Number(p.index) || 0, 0, p.historique.length - 1), brouillon: false, vueLe: Number(p.vueLe) || 0 }));
      const volets = (Array.isArray(s.volets) ? s.volets : []).filter((id) => pages.some((p) => p.id === id)).slice(0, 2);
      if (pages.length) {
        etat = {
          pages, volets: volets.length ? volets : [pages[0].id], actif: borner(Number(s.actif) || 0, 0, Math.max(0, volets.length - 1)),
          ratio: borner(Number(s.ratio) || 50, RATIO_MIN, RATIO_MAX), op: null,
        };
      }
    }
  } catch { /* stockage indisponible ou illisible : espace neuf */ }

  if (!etat) {
    const p = nouvellePage(route);
    return { pages: [p], volets: [p.id], actif: 0, ratio: 50, op: null };
  }
  // Adresse du navigateur (rechargement, lien collé) : page existante, sinon nouvelle page au premier plan.
  const existante = etat.pages.find((p) => routeDe(p) === route);
  if (existante) return marquer(reducteur(etat, { type: 'afficher', page: existante.id }));
  if (route === '/') return etat;
  const plusAncienne = etat.pages.length >= PAGES_MAX
    ? [...etat.pages].filter((p) => !etat!.volets.includes(p.id)).sort((x, y) => x.vueLe - y.vueLe)[0]?.id
    : undefined;
  return { ...reducteur(etat, { type: 'ouvrir', page: nouvellePage(route), aCote: false, fermer: plusAncienne }), entree: undefined, op: null };
}

/* ---------------------------------------------------------------- animations */
// Chorégraphie « nouvelle page » (5 informations, web/README.md › Mouvement ; styles .agx-fantome / .agx-pulse de
// core/app.css) :
//   - ce qui bouge : une copie de la page qui passe en arrière-plan, puis le bouton « Pages » du dock ;
//   - déclencheur : ouverture d'une nouvelle page (Ctrl + clic, ⧉) ;
//   - mouvement : la copie rétrécit et glisse jusqu'au bouton en s'effaçant, puis le bouton pulse (agrandi, remis) ;
//   - durée : copie 1,5 × longue (480 ms) · entrée (elle libère vite la nouvelle page et arrive en douceur à sa place
//     dans le dock) ; pulsation 2 × longue (640 ms) · entrée ;
//   - mouvement réduit : pas de copie ; le compteur du bouton reste cerclé, immobile, le temps de la pulsation.
function pulser(bouton: Element | null) {
  if (!bouton) return;
  bouton.classList.remove('agx-pulse');
  void (bouton as HTMLElement).offsetWidth;
  bouton.classList.add('agx-pulse');
  window.setTimeout(() => bouton.classList.remove('agx-pulse'), DUREE_PULSATION);
}

/** La page qui passe en arrière-plan rétrécit et glisse vers le bouton « Pages » du dock (copie visuelle). */
function animerVersDock(idPage: string | undefined) {
  const bouton = document.querySelector('[data-dock="pages"]');
  const el = idPage ? document.querySelector<HTMLElement>(`[data-page-id="${CSS.escape(idPage)}"]`) : null;
  if (mouvementReduit() || !el || !bouton || typeof el.animate !== 'function') { pulser(bouton); return; }
  const r = el.getBoundingClientRect();
  const haut = Math.max(r.top, 0);
  const bas = Math.min(r.bottom, window.innerHeight);
  if (bas - haut < 40 || r.width < 40) { pulser(bouton); return; }
  // Mesures à l'échelle de l'écran, écrites à l'échelle de la page (zoom de l'affichage).
  const z = zoomRacine(), px = (v: number) => `${v / z}px`;
  const fantome = document.createElement('div');
  fantome.className = 'agx-fantome';
  fantome.setAttribute('aria-hidden', 'true');
  Object.assign(fantome.style, { left: px(r.left), top: px(haut), width: px(r.width), height: px(bas - haut) });
  const copie = el.cloneNode(true) as HTMLElement;
  copie.removeAttribute('data-page-id');
  copie.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
  Object.assign(copie.style, { width: px(r.width), height: px(r.height), marginTop: px(r.top - haut), overflow: 'hidden', animation: 'none' });
  fantome.appendChild(copie);
  document.body.appendChild(fantome);
  copie.scrollTop = el.scrollTop;
  const b = bouton.getBoundingClientRect();
  const dx = b.left + b.width / 2 - (r.left + r.width / 2);
  const dy = b.top + b.height / 2 - (haut + (bas - haut) / 2);
  const echelle = borner(48 / r.width, 0.04, 0.2);
  const anim = fantome.animate(
    [{ transform: 'translate(0, 0) scale(1)', opacity: 1 }, { transform: `translate(${px(dx)}, ${px(dy)}) scale(${echelle})`, opacity: 0.15 }],
    { duration: DUREE_ANIMATION, easing: courbeCss('enter'), fill: 'forwards' },
  );
  anim.onfinish = () => { fantome.remove(); pulser(bouton); };
  anim.oncancel = () => fantome.remove();
}

/* ---------------------------------------------------------------- modificateurs (Ctrl / ⌘) */
let dernierModifieur = { modifie: false, le: 0 };
const noterModifieur = (e: MouseEvent | KeyboardEvent) => { dernierModifieur = { modifie: e.ctrlKey || e.metaKey, le: performance.now() }; };
/** Ctrl / ⌘ enfoncé lors du clic (ou de la touche Entrée) qui a déclenché l'action en cours. */
const modifieurRecent = () => dernierModifieur.modifie && performance.now() - dernierModifieur.le < 250;

/** Lien interne à l'application (chemin absolu, hors écrans de connexion). */
export function lienInterne(cible: EventTarget | null): string | null {
  const a = (cible as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
  if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return null;
  const href = a.getAttribute('href') || '';
  if (!href.startsWith('/') || href.startsWith('//') || estRouteAuth(cheminSeul(href))) return null;
  return href;
}

/* ---------------------------------------------------------------- contexte */
export interface OptionsOuvrir {
  /** true : nouvelle page au premier plan ; false : jamais (même avec Ctrl/⌘). Par défaut : selon Ctrl/⌘. */
  nouvellePage?: boolean;
  /** Ouvre la route dans une nouvelle page affichée à côté de la page active. */
  aCote?: boolean;
  /** Événement d'origine (Ctrl/⌘ ⇒ nouvelle page). */
  evenement?: { ctrlKey: boolean; metaKey: boolean };
  /** Page où naviguer sans nouvelle page (par défaut : la page active ; useOuvrir() fournit celle de l'écran). */
  page?: string;
}

export interface ActionsEspace {
  /** Navigation (page active) ou nouvelle page selon les options / Ctrl-⌘. */
  ouvrir: (route: string, options?: OptionsOuvrir) => void;
  /** Navigation dans une page (null = page active). */
  naviguer: (page: string | null, route: string, remplacer?: boolean) => void;
  aller: (page: string, delta: number) => void;
  afficher: (page: string) => void;
  aCote: (page: string) => void;
  fermer: (page: string) => void;
  seule: (volet: number) => void;
  activer: (volet: number) => void;
  definirRatio: (valeur: number) => void;
  infos: (page: string, infos: Partial<Pick<PageEspace, 'titre' | 'etat' | 'brouillon'>>) => void;
}

export interface EtatEspace extends Omit<Etat, 'op'> {
  pageActive: PageEspace;
  routeActive: string;
  annonce: string;
  /** Titre affiché d'une page (titre de l'écran, sinon déduit de l'adresse). */
  titreDe: (p: PageEspace) => string;
  /** Métier et libellé de module (panneau Pages). */
  infosDe: (p: PageEspace) => { metier: MetierId; module: string };
}

const CtxEtat = React.createContext<EtatEspace | null>(null);
const CtxActions = React.createContext<ActionsEspace | null>(null);

export function useEtatEspace(): EtatEspace {
  const c = React.useContext(CtxEtat);
  if (!c) throw new Error('useEtatEspace hors de EspaceProvider');
  return c;
}

export function useActionsEspace(): ActionsEspace {
  const c = React.useContext(CtxActions);
  if (!c) throw new Error('useActionsEspace hors de EspaceProvider');
  return c;
}

/** Page dans laquelle un composant est rendu (null dans la coquille). */
export const CtxPage = React.createContext<{ id: string } | null>(null);

export function EspaceProvider({ children }: { children: React.ReactNode }) {
  const { t, lib } = useT();
  const confirmer = useConfirmation();
  const { moi } = useSocle();
  const [etat, dispatch] = React.useReducer(reducteur, moi.id, initialiser);
  const [annonce, setAnnonce] = React.useState('');
  const etatRef = React.useRef(etat);
  etatRef.current = etat;

  const titreDe = React.useCallback((p: PageEspace) => p.titre || decrirePage(cheminSeul(routeDe(p)), t, lib).titre, [t, lib]);
  const titreRef = React.useRef(titreDe);
  titreRef.current = titreDe;
  const infosDe = React.useCallback((p: PageEspace) => {
    const d = decrirePage(cheminSeul(routeDe(p)), t, lib);
    const metier = (d.metier || (DIMENSIONS.find((x) => x.id === d.noeud?.id)?.proprietaire as MetierId | undefined) || 'it') as MetierId;
    return { metier, module: d.module || d.titre };
  }, [t, lib]);

  const actions = React.useMemo<ActionsEspace>(() => {
    // Page avec un brouillon non enregistré : confirmation dans une fenêtre du design (Radix AlertDialog).
    const confirmerFermeture = (p: PageEspace) => confirmer({
      titre: t('dock.brouillon'), texte: t('dock.brouillonConfirmer', { titre: titreRef.current(p) }), confirmer: t('dock.fermerPage'), danger: true,
    });
    const ouvrirPage = (route: string, aCote: boolean) => {
      const e0 = etatRef.current;
      // 8 pages : la plus ancienne page non visible se ferme (après confirmation si elle a un brouillon).
      const candidate = e0.pages.length >= PAGES_MAX ? e0.pages.filter((p) => !e0.volets.includes(p.id)).sort((x, y) => x.vueLe - y.vueLe)[0] : undefined;
      const poursuivre = (fermer?: string) => {
        const e = etatRef.current;
        if (!aCote) animerVersDock(e.volets[e.actif]);
        const page = nouvellePage(route);
        dispatch({ type: 'ouvrir', page, aCote, fermer });
        setAnnonce(t('dock.nouvellePage', { titre: titreRef.current(page) }));
      };
      if (candidate?.brouillon) void confirmerFermeture(candidate).then((ok) => { if (ok) poursuivre(candidate.id); });
      else poursuivre(candidate?.id);
    };
    // Changement d'écran dans une page visible (liste → fiche, menu, retour…) : transition de vue du navigateur
    // (transition-ecran.ts). Filtres et recherche (même écran), redirections (remplacement) : sans transition.
    const changerEcran = (page: string, route: (p: PageEspace) => string | undefined, appliquer: () => void) => {
      const e = etatRef.current;
      const p = e.pages.find((x) => x.id === page);
      if (p && e.volets.includes(page) && changementDEcran(p, route(p))) transitionEcran(page, appliquer);
      else appliquer();
    };
    const naviguerPage = (page: string, route: string, remplacer: boolean) => {
      const appliquer = () => dispatch({ type: 'naviguer', page, route, remplacer });
      if (remplacer) appliquer();
      else changerEcran(page, () => route, appliquer);
    };
    return {
      ouvrir: (route, options = {}) => {
        const modifie = options.evenement ? options.evenement.ctrlKey || options.evenement.metaKey : modifieurRecent();
        if (options.aCote) ouvrirPage(route, true);
        else if (options.nouvellePage ?? modifie) ouvrirPage(route, false);
        else { const e = etatRef.current; naviguerPage(options.page ?? e.volets[e.actif], route, false); }
      },
      naviguer: (page, route, remplacer = false) => {
        const e = etatRef.current;
        naviguerPage(page ?? e.volets[e.actif], route, remplacer);
      },
      aller: (page, delta) => changerEcran(page, (p) => p.historique[borner(p.index + delta, 0, p.historique.length - 1)],
        () => dispatch({ type: 'aller', page, delta })),
      afficher: (page) => dispatch({ type: 'afficher', page }),
      aCote: (page) => dispatch({ type: 'aCote', page }),
      fermer: (page) => {
        const p = etatRef.current.pages.find((x) => x.id === page);
        const fermerPage = () => dispatch({ type: 'fermer', page, accueil: nouvellePage('/') });
        if (p?.brouillon) void confirmerFermeture(p).then((ok) => { if (ok) fermerPage(); });
        else fermerPage();
      },
      seule: (volet) => dispatch({ type: 'seule', volet }),
      activer: (volet) => dispatch({ type: 'activer', volet }),
      definirRatio: (valeur) => dispatch({ type: 'ratio', valeur }),
      infos: (page, infos) => dispatch({ type: 'infos', page, infos }),
    };
  }, [t, confirmer]);
  const actionsRef = React.useRef(actions);
  actionsRef.current = actions;

  // Enregistrement de l'espace (sans les brouillons), restauré au prochain chargement / à la reconnexion.
  React.useEffect(() => {
    const id = window.setTimeout(() => {
      const s: Sauvegarde = {
        v: 1, pages: etat.pages.map(({ brouillon: _, ...p }) => p), volets: etat.volets, actif: etat.actif, ratio: etat.ratio,
      };
      try { localStorage.setItem(cleEspace(moi.id), JSON.stringify(s)); } catch { /* stockage indisponible */ }
    }, 250);
    return () => window.clearTimeout(id);
  }, [etat.pages, etat.volets, etat.actif, etat.ratio, moi.id]);

  // L'URL du navigateur suit la page active (nouvelle entrée d'historique pour une navigation, sinon remplacement).
  const pageActive = etat.pages.find((p) => p.id === etat.volets[etat.actif]) || etat.pages[0];
  const routeActive = routeDe(pageActive);
  React.useEffect(() => {
    const e = etatRef.current;
    const etatHistorique = { agora: { page: pageActive.id, index: pageActive.index } };
    const pousser = e.op?.type === 'push' && e.op.page === pageActive.id && adresseCourante() !== routeActive;
    try {
      if (pousser) window.history.pushState(etatHistorique, '', routeActive);
      else window.history.replaceState(etatHistorique, '', routeActive);
    } catch { /* adresse refusée par le navigateur */ }
  }, [routeActive, pageActive.id, pageActive.index]);

  // Retour / avance du navigateur → page et position correspondantes.
  React.useEffect(() => {
    const surRetour = (ev: PopStateEvent) => {
      const s = (ev.state as { agora?: { page?: unknown; index?: unknown } } | null)?.agora;
      dispatch({ type: 'historique', page: typeof s?.page === 'string' ? s.page : '', index: Number(s?.index) || 0, route: adresseCourante() });
    };
    window.addEventListener('popstate', surRetour);
    return () => window.removeEventListener('popstate', surRetour);
  }, []);

  // Fin de l'animation d'apparition de la nouvelle page.
  React.useEffect(() => {
    if (!etat.entree) return;
    const id = window.setTimeout(() => dispatch({ type: 'finEntree' }), mouvementReduit() ? 0 : DUREE_ANIMATION);
    return () => window.clearTimeout(id);
  }, [etat.entree]);

  // Liens : Ctrl/⌘ + clic → nouvelle page (avant React Router) ; clic simple sur un lien du design → navigation
  // dans la page du lien (ou la page active pour la coquille) ; Ctrl+Tab → panneau Pages ; brouillons → alerte.
  React.useEffect(() => {
    const capture = (e: MouseEvent) => {
      noterModifieur(e);
      if (e.button !== 0 || !(e.ctrlKey || e.metaKey) || e.altKey) return;
      const href = lienInterne(e.target);
      if (!href) return;
      e.preventDefault();
      e.stopPropagation();
      actionsRef.current.ouvrir(href, { nouvellePage: true });
    };
    const bulle = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a[href]');
      if (!a) return;
      if (a.getAttribute('href') === '#') { e.preventDefault(); return; }
      const href = lienInterne(e.target);
      if (!href) return;
      e.preventDefault();
      const source = a.closest('[data-page-id]')?.getAttribute('data-page-id') || null;
      actionsRef.current.naviguer(source, href);
    };
    const clavier = (e: KeyboardEvent) => {
      if (e.key === 'Enter') noterModifieur(e);
      if (e.ctrlKey && e.key === 'Tab') { e.preventDefault(); window.dispatchEvent(new CustomEvent('agora:pages')); }
    };
    const quitter = (e: BeforeUnloadEvent) => {
      if (!sortieAutorisee() && etatRef.current.pages.some((p) => p.brouillon)) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('click', capture, true);
    document.addEventListener('click', bulle);
    window.addEventListener('keydown', clavier, true);
    window.addEventListener('beforeunload', quitter);
    return () => {
      window.removeEventListener('click', capture, true);
      document.removeEventListener('click', bulle);
      window.removeEventListener('keydown', clavier, true);
      window.removeEventListener('beforeunload', quitter);
    };
  }, []);

  const valeur = React.useMemo<EtatEspace>(() => ({
    pages: etat.pages, volets: etat.volets, actif: etat.actif, ratio: etat.ratio, entree: etat.entree,
    pageActive, routeActive, annonce, titreDe, infosDe,
  }), [etat.pages, etat.volets, etat.actif, etat.ratio, etat.entree, pageActive, routeActive, annonce, titreDe, infosDe]);

  return (
    <CtxActions.Provider value={actions}>
      <CtxEtat.Provider value={valeur}>{children}</CtxEtat.Provider>
    </CtxActions.Provider>
  );
}

/* ---------------------------------------------------------------- hooks des écrans (exposés par le socle) */

const SOURCES_BROUILLON = new SourcesBrouillon();

/**
 * Marque la page courante comme ayant un brouillon non enregistré (confirmation avant fermeture). Plusieurs composants
 * d'une page peuvent l'appeler (formulaire, fichiers en cours d'envoi) : la page reste en brouillon tant qu'au moins
 * l'un d'eux est actif.
 */
export function useBrouillon(actif: boolean): void {
  const page = React.useContext(CtxPage);
  const actions = React.useContext(CtxActions);
  const [source] = React.useState(() => Symbol('brouillon'));
  React.useEffect(() => {
    if (!page || !actions || !actif) return;
    SOURCES_BROUILLON.activer(page.id, source);
    actions.infos(page.id, { brouillon: true });
    return () => {
      if (SOURCES_BROUILLON.desactiver(page.id, source)) actions.infos(page.id, { brouillon: false });
    };
  }, [actif, page, actions, source]);
}

/**
 * Quitte AGORA pour une autre adresse (`sortir` : connexion Microsoft 365, vérification d'identité en pleine page,
 * déconnexion) : confirmation quand un appel, un enregistrement Scribe (useActiviteEnCours) ou un brouillon serait
 * perdu ; l'alerte du navigateur n'est alors pas redemandée. Résout faux si l'utilisateur reste dans AGORA. Toute
 * redirection hors d'AGORA passe par là, jamais par `window.location` directement.
 */
export function useQuitterAgora(): (sortir: () => void | Promise<void>) => Promise<boolean> {
  const { t } = useT();
  const confirmer = useConfirmation();
  return React.useCallback(async (sortir) => {
    const raisons = activitesEnCours().map((a) => (a === 'appel' ? t('app.sortie.appel') : t('app.sortie.enregistrement')));
    if (SOURCES_BROUILLON.enCours()) raisons.push(t('app.sortie.brouillon'));
    if (raisons.length && !(await confirmer({
      titre: t('app.sortie.titre'), texte: raisons.join(' '), confirmer: t('app.sortie.quitter'), annuler: t('app.sortie.rester'), danger: true,
    }))) return false;
    autoriserSortie();
    await sortir();
    return true;
  }, [t, confirmer]);
}

/** Résumé de l'état de la page affiché dans le panneau Pages (ex. « Recherche : pompe · 3 sélectionnés »). */
export function useEtatPage(resume?: string): void {
  const page = React.useContext(CtxPage);
  const actions = React.useContext(CtxActions);
  React.useEffect(() => {
    if (!page || !actions) return;
    actions.infos(page.id, { etat: resume || undefined });
    return () => actions.infos(page.id, { etat: undefined });
  }, [resume, page, actions]);
}

/** Titre de la page (fiche d'un objet : « CLI-0001 · Biogaz… ») : en-tête, onglet du navigateur, panneau Pages. */
export function useTitrePage(titre?: string): void {
  const page = React.useContext(CtxPage);
  const actions = React.useContext(CtxActions);
  React.useEffect(() => {
    if (!page || !actions) return;
    actions.infos(page.id, { titre: titre || undefined });
    return () => actions.infos(page.id, { titre: undefined });
  }, [titre, page, actions]);
}

/**
 * `ouvrir(route, { nouvellePage, aCote, evenement })` : navigation dans la page de l'écran, ou nouvelle page au
 * premier plan (Ctrl/⌘ enfoncé lors du clic, ou `nouvellePage: true`), ou à côté (`aCote: true`).
 */
export function useOuvrir(): ActionsEspace['ouvrir'] {
  const page = React.useContext(CtxPage);
  const { ouvrir } = useActionsEspace();
  return React.useCallback((route, options) => ouvrir(route, { page: page?.id, ...options }), [page, ouvrir]);
}
